// Event log -> report text. Everything the staff "says" in the live stage is a template filled from the
// simulation's append-only event log (spec §7), so the feed, the debrief and any later language-model
// enrichment all read the same source of truth. Nothing here reaches into ground truth: a report may
// only use what the event carries and what blue's own picture already holds.

import type { Contact, Event, EventKind, Snapshot, Unit } from '../sim/types';
import { TICKS_PER_MIN, TICK_SEC } from '../sim/types';
import type { Grid } from '../sim/grid';
import { card } from '../sim/deck';
import { UNIT_DEFS } from '../sim/scenario';
import type { CardId, UnitType } from '../sim/types';

export interface Report {
  /** Event id, or a negative synthetic id for staff-generated reports such as the periodic SITREP. */
  id: number;
  t: number;
  kind: EventKind;
  /** Who is speaking: a unit callsign, or a staff section (S-2 intelligence, S-3 operations, S-4 logistics). */
  source: string;
  text: string;
  significant: boolean;
  cell?: number;
  /** The unit the event is about, when it is about one unit. Carried through so `src/ui/feed/triage.ts`
   * can group same-kind reports ("6 platoons pinned") by counting distinct units, without re-reading the
   * event log itself. */
  unitId?: number;
  /** A sub-key for kinds where grouping still needs to tell instances apart - a phase line's own name,
   * so "3 platoons crossed PL RED" never merges with a crossing of PL BLUE. */
  detail?: string;
}

export interface ReportCtx {
  units: Map<number, Unit>;
  contacts: Map<number, Contact>;
  grid: Grid | null;
  tick: number;
  /** Last-known type confidence per enemy id, from `contact.new` payloads seen so far. A `contact.lost`
   * event carries only the enemy id, and by the time it reaches the feed the contact itself has already
   * been dropped from `contacts` (`src/sim/detection.ts` drops it before logging the event), so this is
   * the only place left to answer "lost - what?". Optional so every other caller (and every existing
   * test) can keep building a `ReportCtx` without it. */
  enemyTypes?: Map<number, string>;
}

/**
 * The one clock.
 *
 * Everything the player reads is stamped `H+HH:MM` from H-hour, whatever surface it is on: the top bar,
 * the report feed, the debrief logs, the advisor age badges. There used to be three formats - this one,
 * an `H+MM:SS` that printed `H+231:30` four sim hours in, and raw decimal minutes (`231.5`) in the
 * debrief tables - all wearing the same `H+` prefix and none of them agreeing.
 */
export function clockHM(tick: number): string {
  const sec = Math.max(0, Math.floor(tick * TICK_SEC));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `H+${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * The same clock with seconds, for the few columns where seconds are the information: the debrief order
 * log's issue / transmit / execute / done ticks, which are what the price of an order is measured in.
 */
export function clockHMS(tick: number): string {
  const sec = Math.max(0, Math.floor(tick * TICK_SEC));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `H+${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Four-digit grid reference from a terrain cell: the AO is reported on a 100 x 100 grid regardless of
 * its cell resolution (the NTC AO is 300 cells across, so one grid square is three cells, 150 m).
 */
export function gridRef(cell: number | undefined, grid: Grid | null): string {
  if (cell === undefined || !grid) return '----';
  const gx = Math.min(99, Math.floor((grid.cx(cell) * 100) / grid.w));
  const gy = Math.min(99, Math.floor((grid.cy(cell) * 100) / grid.h));
  return `${String(gx).padStart(2, '0')}${String(gy).padStart(2, '0')}`;
}

/**
 * Minutes until the force runs itself dry, from a fuel percentage. Placeholder burn rate for the demo
 * cut: one minute of maneuver per two percent of fuel. Task 18 replaces it with the real consumption
 * rate from logistics.ts.
 */
export function culminationMin(fuelPct: number): number {
  return Math.max(0, Math.round(fuelPct / 2));
}

function callsign(ctx: ReportCtx, unitId: number | undefined): string {
  if (unitId === undefined) return 'S-3';
  return ctx.units.get(unitId)?.callsign ?? `UNIT ${unitId}`;
}

/** A card id as the staff would say it, falling back to the raw id for anything unrecognized. */
export function cardLabel(id: unknown): string {
  try {
    return card(id as CardId).label;
  } catch {
    return String(id ?? 'order');
  }
}

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' ? v : fallback);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/**
 * What blue believes it is looking at, never the enemy's real type: the observer's own confidence as
 * the contact event recorded it, falling back to whatever is in the picture by now.
 */
function contactLabel(ctx: ReportCtx, ev: Event): string {
  const enemyId = num(ev.payload.enemyId, -1);
  const conf = str(ev.payload.typeConf) || ctx.contacts.get(enemyId)?.typeConf || ctx.enemyTypes?.get(enemyId) || 'unknown';
  if (conf === 'unknown') return 'unidentified contact';
  if (conf === 'vehicle') return 'vehicle';
  const label = UNIT_DEFS[conf as UnitType]?.label ?? conf;
  // Acronyms such as UAS (unmanned aircraft system) keep their case; everything else reads as prose.
  return label === label.toUpperCase() ? label : label.toLowerCase();
}

/**
 * One event -> one report card, or null when the event belongs to the machinery rather than the feed
 * (red's reasoning, which the debrief reveals; enemy-side bookkeeping; the transmit tick, which the
 * acknowledgement already covers).
 */
export function reportText(ev: Event, ctx: ReportCtx): Report | null {
  // The feed is blue's radio net. Red's events are ground truth and stay hidden until the debrief.
  if (ev.side !== 0) return null;

  const cs = callsign(ctx, ev.unitId);
  const make = (source: string, text: string, detail?: string): Report => ({
    id: ev.id, t: ev.t, kind: ev.kind, source, text, significant: ev.significant, cell: ev.cell,
    unitId: ev.unitId, detail,
  });

  switch (ev.kind) {
    case 'contact.new': {
      const observer = callsign(ctx, num(ev.payload.observerId, -1));
      const errorM = Math.round(num(ev.payload.errorM));
      const fix = errorM > 0 ? ` (±${errorM} m)` : '';
      return make(observer, `SPOTREP — ${contactLabel(ctx, ev)} vic ${gridRef(ev.cell, ctx.grid)}${fix}`);
    }
    case 'nai.contact':
      return make('S-2', `Contact in ${str(ev.payload.name, 'an NAI')} vic ${gridRef(ev.cell, ctx.grid)}`);
    case 'contact.lost':
      return make('S-2', `Contact lost — ${contactLabel(ctx, ev)} last seen vic ${gridRef(ev.cell, ctx.grid)}`);
    case 'pl.crossed': {
      const plName = str(ev.payload.name, 'a phase line');
      return make(cs, `${cs} crossed ${plName}`, plName);
    }
    case 'unit.pinned':
      return make(cs, `${cs} pinned, heavy fire`);
    case 'unit.unpinned':
      return make(cs, `${cs} free to maneuver again`);
    case 'unit.broken':
      return make(cs, `${cs} combat ineffective, breaking contact`);
    case 'unit.destroyed':
      return make('S-3', `${cs} destroyed`);
    case 'unit.fuelLow':
      return make('S-4', `${cs} fuel ${Math.round(num(ev.payload.fuel))}%, culmination in ~${culminationMin(num(ev.payload.fuel))} min`);
    case 'unit.ammoLow':
      return make('S-4', `${cs} ammunition ${Math.round(num(ev.payload.ammo))}%, resupply required`);
    case 'force.fuelLow':
      return make('S-4', `Task force fuel ${Math.round(num(ev.payload.avgFuel))}%, culmination in ~${culminationMin(num(ev.payload.avgFuel))} min`);
    case 'culminated':
      return make('S-4', `Task force culminated at ${Math.round(num(ev.payload.avgStrength))}% average strength`);
    case 'fire.mission':
      return make('S-3', `Fire mission complete, ${num(ev.payload.volley)} rounds vic ${gridRef(ev.cell, ctx.grid)}`);
    case 'uas.lost':
      return make('S-2', `${cs} airframe lost`);
    case 'order.issued':
      // The unit first, then the card: "A11 — Move" reads as a task given to a callsign, where
      // "Move to A11" read as a destination (the review's finding 5).
      return make('S-3', `${cs} — ${cardLabel(ev.payload.card)} · on the command net`);
    case 'order.tx':
      return null;
    case 'order.received':
      return make(cs, `${cs} acknowledges ${cardLabel(ev.payload.card)}`);
    case 'order.exec':
      return make(cs, `${cs} executing ${cardLabel(ev.payload.card)}`);
    case 'order.done':
      return make(cs, `${cs} reports ${cardLabel(ev.payload.card)} complete`);
    case 'order.cancelled':
      return make('S-3', `${cs} — ${cardLabel(ev.payload.card)} · cancelled`);
    case 'order.rejected':
      return make('S-3', `${cs} — ${cardLabel(ev.payload.card)} · refused: ${str(ev.payload.reason, 'not a legal order')}`);
    case 'net.saturated':
      return make('S-3', `Command net saturated at ${Math.round(num(ev.payload.occupancy) * 100)}% — orders are queueing`);
    case 'phase':
      return make('S-3', `Phase: ${str(ev.payload.phase)}`);
    case 'obj.seized':
      return make('S-3', `${str(ev.payload.name, 'Objective')} seized by ${num(ev.payload.platoons)} platoons`);
    case 'sitrep':
      return make('S-3', str(ev.payload.text, 'SITREP'));
    case 'red.decision':
      return null;
    default:
      return null;
  }
}

/** Sim minutes between the staff's own periodic situation reports (spec §7: every 15 sim minutes). */
export const SITREP_INTERVAL_TICKS = 15 * TICKS_PER_MIN;

/**
 * The staff's own periodic situation report, built by the feed rather than the simulation: how much of
 * the task force is still up, how many contacts are tracked, and which phase the attack is in.
 */
export function sitrepReport(snap: Snapshot): Report {
  const alive = snap.units.filter((u) => u.side === 0 && u.alive);
  const platoons = alive.filter((u) => u.type === 'armor' || u.type === 'mech' || u.type === 'inf').length;
  return {
    // Synthetic reports are keyed off the tick so a re-render never duplicates one.
    id: -snap.tick - 1,
    t: snap.tick,
    kind: 'sitrep',
    source: 'S-3',
    text:
      `SITREP — ${snap.phase}. ${platoons} maneuver platoons up, ` +
      `${Math.round(snap.force.strength)}% average strength, fuel ${Math.round(snap.force.fuel)}%. ` +
      `${snap.picture.contacts.length} enemy contacts tracked.`,
    significant: true,
  };
}
